/**
 * Runtime helpers that build a `JoinTableProbe` from a live engine + IPC
 * lookup, then run `shouldCollapseJoinTable`.
 *
 * Slice 5 — wired into the EP6 mini-grid drill path so the pushed table-node
 * is auto-collapsed when the referenced table is a pure m2m join. The probe
 * runs once per drill (not memoized) because it fires at most a handful of
 * times per Single-Row View session; Slice 6 will move it onto a schema-level
 * cache once the schema store grows a sidecar map.
 */

import {
  shouldCollapseJoinTable,
  type JoinTableProbe,
  type JoinTableProbeColumn,
} from './joinTableCollapse';
import type { JoinTableHint } from './joinTableHintTypes';
import type {
  ConstraintMetadata,
  JoinTableHintRecord,
  TableMetadata,
} from '@/types/electron';

export type ProbeJoinCollapseArgs = {
  readonly connectionId: string | null | undefined;
  readonly database: string | undefined;
  readonly schema: string;
  readonly table: string;
  readonly hint: JoinTableHint | undefined;
};

/** Look up the MetaInfo hint for a given (schema, table); undefined when absent. */
export function pickJoinTableHint(
  hints: ReadonlyArray<JoinTableHintRecord> | undefined,
  schema: string,
  table: string,
): JoinTableHint | undefined {
  if (!hints || hints.length === 0) return undefined;
  const match = hints.find(
    (h) => h.schema === schema && h.table === table,
  );
  return match?.hint;
}

function fkColumnsFromConstraints(
  constraints: ConstraintMetadata[] | undefined,
): { columns: string[]; constraintCount: number } {
  if (!constraints) return { columns: [], constraintCount: 0 };
  const cols: string[] = [];
  let count = 0;
  for (const c of constraints) {
    if (c.type !== 'FOREIGN KEY') continue;
    count += 1;
    for (const col of c.columns) cols.push(col);
  }
  return { columns: cols, constraintCount: count };
}

function pkColumnsFromConstraints(
  constraints: ConstraintMetadata[] | undefined,
): string[] {
  if (!constraints) return [];
  for (const c of constraints) {
    if (c.type === 'PRIMARY KEY') return [...c.columns];
  }
  return [];
}

function uniqueSetsFromConstraints(
  constraints: ConstraintMetadata[] | undefined,
): string[][] {
  if (!constraints) return [];
  return constraints
    .filter((c) => c.type === 'UNIQUE')
    .map((c) => [...c.columns]);
}

/**
 * Build a `JoinTableProbe` from a `TableMetadata` snapshot + inbound-FK
 * count. The two arguments are typically supplied by IPC calls in the
 * caller; this function is pure and unit-testable.
 */
export function buildProbeFromMetadata(
  metadata: TableMetadata | undefined,
  inboundFkCount: number,
): JoinTableProbe | null {
  if (!metadata) return null;
  const { columns: fkCols, constraintCount } = fkColumnsFromConstraints(
    metadata.constraints,
  );
  const pkCols = pkColumnsFromConstraints(metadata.constraints);
  const uniqueSets = uniqueSetsFromConstraints(metadata.constraints);
  const probeColumns: JoinTableProbeColumn[] = metadata.columns.map((c) => ({
    name: c.name,
    dataType: c.dataType,
    isPrimaryKey: c.isPrimaryKey,
    isIdentity: c.isIdentity,
  }));
  return {
    outboundFkColumns: fkCols,
    outboundFkCount: constraintCount,
    inboundFkCount,
    primaryKeyColumns: pkCols,
    uniqueColumnSets: uniqueSets,
    columns: probeColumns,
  };
}

/**
 * Fire-and-forget probe runner. Returns false on any failure (IPC unavailable,
 * fetch fails, metadata empty) — we never block the drill on a probe error.
 */
export async function probeJoinCollapse(
  args: ProbeJoinCollapseArgs,
): Promise<boolean> {
  // Short-circuit when the hint already forces an answer — no fetch needed.
  if (args.hint === 'collapse') return true;
  if (args.hint === 'expand') return false;

  const { connectionId, database, schema, table } = args;
  if (!connectionId || !database) return false;

  const api = window.electronAPI;
  if (!api?.getTableMetadata) return false;
  try {
    const meta = await api.getTableMetadata({
      connectionId,
      database,
      schema,
      tableName: table,
    });
    if (!meta?.success || !meta.metadata) return false;

    // Inbound FK count via the existing IPC. The result includes both real
    // and loose inbound FKs; for the m2m rule we only care about the count.
    let inboundFkCount = 0;
    if (api.tableExplorer?.fetchInboundForeignKeys) {
      try {
        const ibRes = await api.tableExplorer.fetchInboundForeignKeys({
          connectionId,
          table: { database, schema, tableName: table },
        });
        if (ibRes?.success && Array.isArray(ibRes.foreignKeys)) {
          inboundFkCount = ibRes.foreignKeys.length;
        }
      } catch {
        // Treat probe failure as "not a join" — never block the drill.
        return false;
      }
    }

    const probe = buildProbeFromMetadata(meta.metadata, inboundFkCount);
    if (!probe) return false;
    return shouldCollapseJoinTable(probe, args.hint);
  } catch {
    return false;
  }
}
